import { Box, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { DimensionKey } from '../../../../queries/recognition-analysis';

export type DraftAssistCardProps = {
	suggestion: string;
	dimensionKey: DimensionKey;
	isLoading: boolean;
	onAdd: () => void;
	onDismiss: () => void;
};

const PROMPT_GRADIENT = 'linear-gradient(130deg, #C5E8BA 0%, #70C481 24%, #B3EBE8 55%, #9783EA 100%)';

const suggestionTileSx = {
	border: '1px solid transparent',
	backgroundImage: `linear-gradient(var(--chakra-colors-bg-surface), var(--chakra-colors-bg-surface)), ${PROMPT_GRADIENT}`,
	backgroundOrigin: 'padding-box, border-box',
	backgroundClip: 'padding-box, border-box',
	cursor: 'pointer',
	'&:hover': {
		border: '1px solid transparent',
		backgroundImage: `linear-gradient(var(--chakra-colors-bg-subtle), var(--chakra-colors-bg-subtle)), ${PROMPT_GRADIENT}`,
	},
};

export const DraftAssistCard: FC<DraftAssistCardProps> = ({ suggestion, isLoading, onAdd, onDismiss }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge.draftAssist' });

	return (
		<Box my={2} data-testid="draft-assist-card">
			{/* Label above the suggestion tile */}
			<Text fontSize="xs" color="text.softer" mb={1.5}>
				{t('trySomethingLike')}
			</Text>

			<VStack spacing={2} align="stretch">
				{/* Fully clickable suggestion tile */}
				<Box
					borderRadius="12px"
					px={3}
					py={2}
					sx={suggestionTileSx}
					minH="1.25rem"
					onClick={onAdd}
					role="button"
					aria-label={t('addButton')}
					data-testid="draft-assist-tile"
				>
					<AnimatePresence mode="wait">
						{isLoading ? (
							<motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
								<Spinner size="xs" color="hashtag.fgOnSurface" data-testid="draft-assist-spinner" />
							</motion.div>
						) : (
							<motion.div key="suggestion" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
								<Text fontSize="sm" color="text.softer" fontWeight="normal">
									{suggestion}
								</Text>
							</motion.div>
						)}
					</AnimatePresence>
				</Box>

				{/* Dismiss link */}
				<Box display="flex" justifyContent="flex-end">
					<Link
						fontSize="xs"
						color="text.softer"
						textDecoration="none"
						_hover={{ textDecoration: 'underline' }}
						cursor="pointer"
						onClick={onDismiss}
						data-testid="draft-assist-cancel"
					>
						{t('cancel')}
					</Link>
				</Box>
			</VStack>
		</Box>
	);
};
